import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  getRuntimeBlockReason,
  initTracker,
  isProductionRuntime,
} from './common/services/analytics/analytics';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { PersistGate } from 'redux-persist/integration/react';
import './styles/index.scss';
import { BrowserRouter, useLocation } from 'react-router-dom';
import reportWebVitals from './reportWebVitals';
import AppRoutes from './app/app.routes';
import { theme } from './material-theme';
import { persistor, store } from './store/create-store';
import ENVIRONMENTS from './environments';
import AlertProvider from './common/providers/alert.provider';
import UserDataProvider from './common/contexts/user-data.context';
import NotificationProvider from './common/contexts/notification-data.context';
import AppDataProvider from './common/contexts/app-data.context';
import { bootstrapDashboardPreferences } from './common/preferences/dashboard-preferences';
import CursorOverlayGuard from './common/dev/cursor-overlay-guard.component';
import AppErrorBoundary from './common/components/app-error-boundary/app-error-boundary.component';
import { startSessionKeeper } from './common/lib/session-keeper';

// eslint-disable-next-line import/no-extraneous-dependencies, @typescript-eslint/no-var-requires
const { stopReportingRuntimeErrors } = require('react-error-overlay');

bootstrapDashboardPreferences();
startSessionKeeper();

function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
}

const queryClient = new QueryClient();

try {
  if (process?.env?.NODE_ENV === 'development') {
    stopReportingRuntimeErrors(); // disables error overlays
  }
  if (isProductionRuntime()) {
    initTracker({
      projectKey: ENVIRONMENTS.OPENREPLAY_PROJECT_KEY,
      ingestPoint: ENVIRONMENTS.OPENREPLAY_INGEST_POINT,
    });
  } else {
    console.log('[OpenReplay] not started:', getRuntimeBlockReason());
  }
} catch (e) {
  console.log(e);
}

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <React.StrictMode>
        <StyledEngineProvider injectFirst>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <AlertProvider>
                <BrowserRouter>
                  <AppDataProvider>
                    <UserDataProvider>
                      <NotificationProvider>
                        <CursorOverlayGuard />
                        <RoutedErrorBoundary>
                          <AppRoutes />
                        </RoutedErrorBoundary>
                      </NotificationProvider>
                    </UserDataProvider>
                  </AppDataProvider>
                </BrowserRouter>
              </AlertProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </StyledEngineProvider>
      </React.StrictMode>
    </PersistGate>
  </Provider>,
  document.getElementById('root'),
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const isProduction = process.env.NODE_ENV === 'production';
    if (!isProduction) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
      return;
    }

    fetch('/service-worker.js', { method: 'HEAD' })
      .then(response => {
        const contentType = response.headers.get('content-type') || '';
        const isJs =
          response.ok && /javascript|ecmascript/i.test(contentType);
        if (!isJs) {
          throw new Error(
            `service-worker.js is not JavaScript (${response.status} ${contentType})`,
          );
        }
        return navigator.serviceWorker.register('/service-worker.js');
      })
      .then(registration => {
        if (!registration) return;
        console.log('SW registered: ', registration);
        registration.update();
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

// Learn more: https://bit.ly/CRA-vitals
// eslint-disable-next-line no-console
reportWebVitals(console.log);
